/**
 * Ollama Local LLM Integration Service
 * Runs on your machine for voice, persona, and learning
 * Free and fully offline-capable
 * https://ollama.ai
 */

export const ollamaService = {
  baseUrl: 'http://localhost:11434',
  model: 'deepseek-r1:8b', // Using Deepseek model - also supports: mistral, llama2, dolphin
  voiceModel: 'deepseek-r1:8b', // Model to use for voice/persona
  trainingDataFile: 'ai_training_data.json',

  /**
   * Set base URL for Ollama service
   */
  setBaseUrl(url) {
    this.baseUrl = url
  },

  /**
   * Ping Ollama to check availability
   */
  async ping() {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`)
      if (!response.ok) throw new Error('Ollama not responding')
      return true
    } catch (error) {
      console.error('Ollama ping failed:', error)
      throw error
    }
  },

  /**
   * Analyze data using local model
   */
  async analyzeData(data, context = '') {
    try {
      const prompt = `Analyze the following data and provide detailed insights:

Context: ${context}
Data: ${JSON.stringify(data, null, 2)}

Provide:
1. Key findings
2. Anomalies detected
3. Recommendations
4. Risk assessment`

      return await this._callOllama(prompt, this.model)
    } catch (error) {
      console.error('Ollama analysis failed:', error)
      throw error
    }
  },

  /**
   * Generate insights from metrics
   */
  async generateInsights(metrics, category = 'general') {
    try {
      const prompt = `Generate ${category} business insights from these metrics:
${JSON.stringify(metrics, null, 2)}

Format as JSON array with objects containing: {type, text, priority}`

      const response = await this._callOllama(prompt, this.model)

      try {
        return JSON.parse(response)
      } catch (e) {
        return [{ type: 'neutral', text: response, priority: 'low' }]
      }
    } catch (error) {
      console.error('Ollama insights generation failed:', error)
      throw error
    }
  },

  /**
   * Generate recommendations using local model
   */
  async generateRecommendations(situation, context = {}) {
    try {
      const prompt = `Based on this situation, generate strategic recommendations:

Situation: ${situation}
Context: ${JSON.stringify(context, null, 2)}

For each recommendation provide: action, rationale, impact, timeline.
Format as JSON array.`

      const response = await this._callOllama(prompt, this.model)

      try {
        return JSON.parse(response)
      } catch (e) {
        return [
          {
            action: 'Review and analyze',
            rationale: response,
            impact: 'medium',
            timeline: 'Immediate',
          },
        ]
      }
    } catch (error) {
      console.error('Ollama recommendations failed:', error)
      throw error
    }
  },

  /**
   * Process alerts using local model
   */
  async processAlert(alertData, severity = 'medium') {
    try {
      const prompt = `Process this ${severity} severity alert:
${JSON.stringify(alertData, null, 2)}

Determine appropriate action. Response format:
{
  "action": "alert|investigate|log",
  "priority": "critical|high|medium|low",
  "reason": "explanation",
  "recommendations": ["list", "of", "actions"]
}`

      const response = await this._callOllama(prompt, this.model)

      try {
        return JSON.parse(response)
      } catch (e) {
        return {
          action: 'log',
          priority: severity,
          reason: response,
          recommendations: [],
        }
      }
    } catch (error) {
      console.error('Ollama alert processing failed:', error)
      throw error
    }
  },

  /**
   * Generate voice/persona response
   * This is where YOUR AI personality comes through
   */
  async generateVoiceResponse(userInput, personaContext = {}, conversationHistory = []) {
    try {
      // Build context from recent conversation
      const historyText = conversationHistory
        .slice(-5) // Last 5 messages
        .map(msg => `${msg.role}: ${msg.content}`)
        .join('\n')

      const prompt = `You are an AI assistant with this personality:
${JSON.stringify(personaContext, null, 2)}

Recent conversation:
${historyText}

User: ${userInput}

Respond naturally as yourself. Be helpful, friendly, and authentic. Keep it concise.`

      return await this._callOllama(prompt, this.voiceModel, {
        temperature: 0.8, // Slightly creative for personality
        num_predict: 256, // Reasonable length for voice
      })
    } catch (error) {
      console.error('Ollama voice response failed:', error)
      throw error
    }
  },

  /**
   * Generate adaptive response that learns user preferences
   */
  async generateAdaptiveResponse(userMessage, userPattern = {}, conversationHistory = []) {
    try {
      // Build adaptation based on learned patterns
      const adaptationInstructions = this._buildAdaptationInstructions(userPattern)

      const prompt = `${adaptationInstructions}

User message: "${userMessage}"

Respond in a way that matches the user's preferences and communication style.`

      return await this._callOllama(prompt, this.voiceModel, {
        temperature: 0.7,
        num_predict: 512,
      })
    } catch (error) {
      console.error('Ollama adaptive response failed:', error)
      throw error
    }
  },

  /**
   * Record training data for fine-tuning
   * Builds dataset from user interactions for model improvement
   */
  async recordTrainingData(interaction, feedback) {
    try {
      // In a real system, this would save to a training dataset
      // that could be used to fine-tune the local model
      const trainingExample = {
        timestamp: new Date().toISOString(),
        input: interaction.input,
        output: interaction.output,
        feedback: feedback,
        quality_score: feedback.rating || 0.5,
      }

      // Store in localStorage for now
      const existing = JSON.parse(localStorage.getItem('ai_training_data') || '[]')
      existing.push(trainingExample)
      localStorage.setItem('ai_training_data', JSON.stringify(existing.slice(-100))) // Keep last 100

      console.log('[Ollama] Training data recorded:', trainingExample)

      // In production, you would:
      // 1. Send this to your backend
      // 2. Accumulate enough data
      // 3. Fine-tune the model with: ollama create custom-model --from neural-chat --embed training_data.jsonl

      return { recorded: true, dataPoints: existing.length }
    } catch (error) {
      console.error('Failed to record training data:', error)
      throw error
    }
  },

  /**
   * Clear training data (privacy control)
   */
  async clearTrainingData() {
    localStorage.removeItem('ai_training_data')
    return { cleared: true }
  },

  /**
   * Get available models on local Ollama
   */
  async getAvailableModels() {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`)
      const data = await response.json()
      return data.models || []
    } catch (error) {
      console.error('Failed to get models:', error)
      return []
    }
  },

  /**
   * Switch to a different local model
   */
  setModel(modelName) {
    this.model = modelName
    return { model: modelName, baseUrl: this.baseUrl }
  },

  /**
   * Analyze image using llava vision model
   * Requires: ollama pull llava
   */
  async analyzeImage(imageBase64, context = 'Analyze this image') {
    try {
      // Use llava model for image analysis
      const payload = {
        model: 'llava',
        prompt: context,
        images: [imageBase64], // Base64 encoded image
        stream: false,
      }

      const response = await fetch(`${this.baseUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Image analysis failed: ${response.statusText}`)
      }

      const data = await response.json()
      return data.response
    } catch (error) {
      console.error('Image analysis failed:', error)
      // Fallback if llava not available
      return `[Image analysis unavailable: ${error.message}]. Please describe the image manually.`
    }
  },

  /**
   * Internal method to call Ollama API
   */
  async _callOllama(prompt, model = this.model, options = {}) {
    try {
      const payload = {
        model: model,
        prompt: prompt,
        stream: false,
        ...options,
      }

      const response = await fetch(`${this.baseUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Ollama API error: ${response.statusText}`)
      }

      const data = await response.json()
      return data.response
    } catch (error) {
      console.error('Ollama API call failed:', error)
      throw error
    }
  },

  /**
   * Build adaptation instructions based on learned user patterns
   */
  _buildAdaptationInstructions(userPattern = {}) {
    let instructions = 'Adapt your response based on these user preferences:\n'

    if (userPattern.preferredTone) {
      instructions += `- Tone: ${userPattern.preferredTone}\n`
    }
    if (userPattern.detailLevel) {
      instructions += `- Detail level: ${userPattern.detailLevel} (brief/moderate/detailed)\n`
    }
    if (userPattern.communicationStyle) {
      instructions += `- Communication style: ${userPattern.communicationStyle}\n`
    }
    if (userPattern.responseLength) {
      instructions += `- Preferred response length: ${userPattern.responseLength}\n`
    }
    if (userPattern.technicalLevel) {
      instructions += `- Technical level: ${userPattern.technicalLevel} (basic/intermediate/advanced)\n`
    }

    return instructions
  },
}

export default ollamaService
